import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  BrandingImageDimension,
  BrandingImageSettings
} from '@models/adminstration/organization/branding/branding-image-settings';
import {BrandingService} from '@services/administration/organization/configuration/branding/branding.service';
import {BrandingImagePopoverDetails} from '@models/adminstration/organization/branding/branding-image-popover-details';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-organization-branding-image',
    templateUrl: './branding-image.component.html',
    styleUrls: ['./branding-image.component.scss'],
    standalone: false
})
export class BrandingImageComponent implements OnInit, OnDestroy {
  @Input()
  logoDetails: BrandingImageSettings | undefined;

  @Input()
  image: string = '';

  @Input()
  parentImage: string = '';

  @Input()
  background: string = '';

  @Input()
  isAdminRole: boolean = false;
  @Input()
  isSuperAdminRole: boolean = false;
  @Input()
  brandingIsLoading: boolean = true;

  @Output()
  imageChangedEvent = new EventEmitter<string>();

  name: string = '';
  title: string = '';
  imageType: string = '';
  imageMimeTypes: string[] = new Array<string>();
  defaultUrl: string = '';
  recommendedSize: BrandingImageDimension = new BrandingImageDimension(0, 0);
  popoverDetails: BrandingImagePopoverDetails = new BrandingImagePopoverDetails('', '');
  private destroy$: Subject<boolean> = new Subject<boolean>();


  constructor(private brandingService: BrandingService,
              private loadingIndicator: LoadingIndicatorService,
              private translate: TranslateService,
              private notification: NotificationService) {
  }

  ngOnInit(): void {
    this.initProperties();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private initProperties(): void {
    if (this.logoDetails != undefined) {
      this.name = this.logoDetails.name;
      this.title = this.logoDetails.label;
      this.imageType = this.logoDetails.type;
      this.imageMimeTypes = this.logoDetails.mimeTypes;
      this.defaultUrl = this.logoDetails.url;
      this.recommendedSize = this.logoDetails.size;

      this.popoverDetails = this.brandingService.getImagePopoverDetailsByType(this.logoDetails.name);
    }
  }


  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.dataset['fallbackApplied'] === 'true') {
      return;
    }
    img.dataset['fallbackApplied'] = 'true';
    img.src = this.defaultUrl;
  }

  onResetImage() {
    const resetPromise = this.brandingService.verifyAndSetImageRequiredDimensions(this.parentImage, this.name, this.title, this.recommendedSize);
    resetPromise.pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.image = success;
      this.imageChangedEvent.emit(success);
    }, (error) => {
      this.notification.error(this.translate.instant('There was a failure setting image defaults'), error, 'BrandingImageComponent', true);
    });
  }

  onFileSelected($event: Event) {
    if (!$event.target) {
      return;
    }
    const target = $event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return;
    }
    this.loadingIndicator.show(this.translate.instant('Loading logo image'));

    const file = target.files[0];
    if (!file) {
      this.loadingIndicator.hide();
      return;
    }

    if (this.imageMimeTypes.indexOf(file.type.toLowerCase()) < 0) {
      let message = this.translate.instant('Uploaded file type "#1" is not "#2"');
      message = message.replace('#1', file.type);
      message = message.replace('#2', this.imageType);
      this.notification.warning(message, 'BrandingImage');
      this.loadingIndicator.hide();
      return;
    }

    const r = new FileReader();
    r.readAsDataURL(file);
    r.onloadend = () => {
      this.image = String(r.result);
      this.imageChangedEvent.emit(this.image);
      this.loadingIndicator.hide();
    };


  }
}
